// src/pages/calendar/EventDetail.tsx -- Phase 9 / Cut 2.
//
// Calendar event detail screen. Cut 1 received the full CalendarEvent as a
// prop from the list state. Cut 2 takes only an event id and fetches
// /api/calendar/events/{id} on mount -- the list omits `schedule` to keep
// payloads small, so the detail roundtrip is where the timetable + turns
// arrive.
//
// Composition: TopBar -> DetailHero -> KeyInfoCard -> ScheduleBlock.
// The Pin button in the TopBar is a silent no-op in V1 per gotcha #15 --
// affordance preserved; behaviour deferred.
//
// Loading / error states:
//   * Pending fetch -> full-screen skeleton matching the WeeklyPage shape.
//   * 404 -> "Event no longer available" card with a Back button (the row
//     may have been removed by a sync between the list fetch and the
//     detail tap). Specific case worth a dedicated message rather than
//     the generic error path.
//   * Other errors -> generic error card with the message + Back button.

import { useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow, Avatar, Stamp, TopBar, Icons, DoubleRule } from "../../components/ui";
import { CAL_CATEGORIES, TONES, safeTone, type CalCategory } from "./categories";
import type { CalendarEvent, CalendarService, CalendarTurn } from "./types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format an inclusive date range for display. Mirrors the prototype's
 *  fmtRange so the visual contract is exact. */
function fmtRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const sd = s.getDate();
  const ed = e.getDate();
  const sm = s.toLocaleDateString("en-GB", { month: "short" });
  const em = e.toLocaleDateString("en-GB", { month: "short" });
  const sy = s.getFullYear();
  const ey = e.getFullYear();
  if (start === end) return `${sd} ${sm} ${sy}`;
  if (sm === em && sy === ey) return `${sd}–${ed} ${sm} ${sy}`;
  if (sy === ey) return `${sd} ${sm} – ${ed} ${em} ${sy}`;
  return `${sd} ${sm} ${sy} – ${ed} ${em} ${ey}`;
}

/** Inclusive day-count between two YYYY-MM-DD strings. */
function inclusiveDays(start: string, end: string): number {
  const s = new Date(start + "T00:00:00").getTime();
  const e = new Date(end + "T00:00:00").getTime();
  return Math.round((e - s) / 86400000) + 1;
}

// Loco icon -- production Icons doesn't export one. Inlined SVG with the
// same path the prototype uses on the train-turn cards.
function LocoIcon({ size = 14, color = C.brassDk }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M6 5h12a2 2 0 0 1 2 2v9a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7a2 2 0 0 1 2-2z" />
      <path d="M4 11h16" />
      <path d="M8 21l-2-3" />
      <path d="M16 21l2-3" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Key Information row
// ---------------------------------------------------------------------------

function KeyRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "110px 1fr",
        gap: 14,
        padding: "12px 0",
        borderTop: `1px solid ${C.hairlineLt}`,
      }}
    >
      <div
        style={{
          fontFamily: F.body,
          fontSize: 10.5,
          fontWeight: 800,
          color: C.inkMute,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          paddingTop: 3,
        }}
      >
        {label}
      </div>
      <div style={{ minWidth: 0, color: C.ink, fontWeight: 600, fontSize: 14.5, lineHeight: 1.4 }}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Build progress bar
// ---------------------------------------------------------------------------

function ProgressBar({ pct, cat }: { pct: number; cat: CalCategory }) {
  const t = TONES[safeTone(cat.tone)];
  const done = pct >= 100;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 5 }}>
        <span
          style={{
            fontFamily: F.mono,
            fontSize: 18,
            fontWeight: 700,
            color: done ? C.sageDk : t.fg,
            letterSpacing: 0.4,
          }}
        >
          {pct}%
        </span>
        <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12, fontWeight: 600, color: C.inkMute }}>
          {done ? "Complete" : pct >= 75 ? "Nearly there" : pct >= 40 ? "In progress" : "Early stages"}
        </span>
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 6,
          background: C.paperWarm,
          overflow: "hidden",
          border: `1px solid ${C.hairlineLt}`,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: done
              ? `linear-gradient(90deg, ${C.sage}, ${C.sageDk})`
              : `linear-gradient(90deg, ${t.stripe}, ${t.solid})`,
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Key Info card
// ---------------------------------------------------------------------------

function KeyInfoCard({ event, cat }: { event: CalendarEvent; cat: CalCategory }) {
  const days = inclusiveDays(event.start, event.end);
  const tone = safeTone(cat.tone);
  const t = TONES[tone];
  return (
    <Card padded>
      <Eyebrow>Key information</Eyebrow>
      <div
        style={{
          fontFamily: F.serif,
          fontSize: 24,
          fontWeight: 700,
          color: C.ink,
          letterSpacing: -0.4,
          lineHeight: 1.1,
          marginTop: 2,
        }}
      >
        {event.name}
      </div>
      <div style={{ marginTop: 10, padding: "0 2px" }}>
        <KeyRow label="Event type">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: t.solid,
                boxShadow: `0 0 0 2px ${t.bg}`,
              }}
            />
            {cat.label}
          </span>
        </KeyRow>
        <KeyRow label="Dates">{fmtRange(event.start, event.end)}</KeyRow>
        <KeyRow label="Operating days">
          <span style={{ fontFamily: F.mono, fontSize: 15, fontWeight: 700, color: C.brassDk }}>
            {event.operatingDays}
          </span>
          <span style={{ color: C.inkMute, fontWeight: 600, marginLeft: 6, fontSize: 13 }}>
            {event.operatingDays === 0 ? "(no public services)" : `of ${days} ${days === 1 ? "day" : "days"}`}
          </span>
        </KeyRow>
        <KeyRow label="Location">{event.location}</KeyRow>
        <KeyRow label="Capacity">
          {event.capacity > 0 ? (
            <span>
              <span style={{ fontFamily: F.mono, fontSize: 15, fontWeight: 700, color: C.brassDk }}>
                {event.capacity.toLocaleString()}
              </span>{" "}
              <span style={{ color: C.inkMute, fontWeight: 600, fontSize: 13 }}>visitors</span>
            </span>
          ) : (
            <span style={{ color: C.inkMute, fontStyle: "italic", fontFamily: F.serif }}>n/a</span>
          )}
        </KeyRow>
        <KeyRow label="Event manager">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Avatar in production only accepts brass / heather / sage / signal.
                safeTone already collapsed ink -> brass; collapse paper too so
                the Visiting Loco category gets a brass avatar rather than a
                tsc error. */}
            <Avatar first={event.manager.first} last={event.manager.last} size={32} tone={tone === "paper" ? "brass" : tone} />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: F.serif,
                  fontSize: 15,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: -0.1,
                  lineHeight: 1.1,
                }}
              >
                {event.manager.first} {event.manager.last}
              </div>
              <div
                style={{
                  fontFamily: F.mono,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: C.brassDk,
                  letterSpacing: 1,
                  marginTop: 2,
                }}
              >
                {event.manager.initials}
              </div>
            </div>
          </div>
        </KeyRow>
        <div style={{ padding: "12px 0 4px", borderTop: `1px solid ${C.hairlineLt}` }}>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 10.5,
              fontWeight: 800,
              color: C.inkMute,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Build progress
          </div>
          <ProgressBar pct={event.buildProgress} cat={cat} />
        </div>
        {event.notes && (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              background: C.paperWarm,
              borderRadius: 10,
              border: `1px dashed ${C.hairline}`,
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 13.5,
              fontWeight: 600,
              color: C.inkSoft,
              lineHeight: 1.5,
            }}
          >
            {event.notes}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Crew chips (2-char strings rendered verbatim, gotcha #8)
// ---------------------------------------------------------------------------

function CrewChips({ crew }: { crew: string[] }) {
  if (crew.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      {crew.map((c, i) => (
        <span
          key={i}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 6,
            background: C.paperWarm,
            color: C.brassDk,
            border: `1px solid ${C.hairline}`,
            fontFamily: F.mono,
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 0.3,
          }}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service row -- one row per CalendarService inside the Daily Timetable card
// ---------------------------------------------------------------------------

function ServiceRow({ service, idx }: { service: CalendarService; idx: number }) {
  // booked is null in V1 (gotcha #10) -- render "— / capacity", never "0 / capacity"
  const pct =
    service.booked != null && service.capacity > 0
      ? Math.round((service.booked / service.capacity) * 100)
      : null;
  const bookFill =
    pct == null
      ? C.inkMute
      : pct >= 95
      ? C.signalDk
      : pct >= 75
      ? C.brassDk
      : C.sageDk;
  return (
    <div
      style={{
        padding: "12px 14px",
        borderTop: idx === 0 ? "none" : `1px dashed ${C.hairlineLt}`,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div style={{ textAlign: "center", paddingTop: 2 }}>
        <div
          style={{
            fontFamily: F.mono,
            fontSize: 17,
            fontWeight: 700,
            color: C.brassDk,
            letterSpacing: 0.4,
            lineHeight: 1,
          }}
        >
          {service.time}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 9.5,
            fontWeight: 800,
            color: C.inkMute,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            marginTop: 4,
          }}
        >
          {service.name}
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 15,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: -0.1,
            lineHeight: 1.15,
          }}
        >
          {service.loco}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 4,
          }}
        >
          <span>{service.from}</span>
          <span style={{ color: C.brassDk, fontWeight: 800 }}>→</span>
          <span>{service.to}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.inkSoft, letterSpacing: 0.4 }}>
              {service.booked != null ? (
                <>
                  <span style={{ color: bookFill }}>{service.booked}</span>
                  <span style={{ color: C.inkMute }}> / {service.capacity}</span>
                </>
              ) : (
                <span style={{ color: C.inkMute }}>— / {service.capacity}</span>
              )}
            </span>
            {pct != null && (
              <div
                style={{
                  width: 54,
                  height: 5,
                  borderRadius: 5,
                  background: C.paperWarm,
                  overflow: "hidden",
                  border: `1px solid ${C.hairlineLt}`,
                }}
              >
                <div style={{ height: "100%", width: `${pct}%`, background: bookFill }} />
              </div>
            )}
          </div>
          <CrewChips crew={service.crew} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Train turn card
// ---------------------------------------------------------------------------

function TurnCard({ turn, cat }: { turn: CalendarTurn; cat: CalCategory }) {
  const t = TONES[safeTone(cat.tone)];
  return (
    <div
      style={{
        borderRadius: 12,
        background: C.card,
        border: `1px solid ${C.hairlineLt}`,
        boxShadow: C.shadowS,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* category accent stripe down the left edge */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: `linear-gradient(180deg, ${t.stripe}, ${t.solid})`,
        }}
      />
      <div style={{ padding: "12px 14px 12px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <LocoIcon size={16} />
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 17,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: -0.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {turn.loco}
            </div>
          </div>
          <Stamp tone="paper">{turn.count} runs</Stamp>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 6,
            fontFamily: F.body,
          }}
        >
          <span style={{ fontFamily: F.mono, color: C.brassDk, fontWeight: 700 }}>{turn.span}</span>
          <span style={{ color: C.inkMute }}>·</span>
          <span>{turn.route}</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 10 }}>
          {turn.services.map((s, i) => {
            const sp = s.indexOf(" ");
            const time = sp >= 0 ? s.slice(0, sp) : s;
            const rest = sp >= 0 ? s.slice(sp + 1) : "";
            return (
              <span
                key={i}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "3px 8px",
                  borderRadius: 6,
                  background: C.paperWarm,
                  border: `1px solid ${C.hairlineLt}`,
                }}
              >
                <span style={{ fontFamily: F.mono, fontSize: 10.5, fontWeight: 700, color: C.brassDk, letterSpacing: 0.3 }}>
                  {time}
                </span>
                {rest && (
                  <span
                    style={{
                      fontFamily: F.body,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: C.inkSoft,
                      letterSpacing: 0.2,
                    }}
                  >
                    {rest}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule block -- placeholder card when schedule is null (gotcha #7)
// ---------------------------------------------------------------------------

function ScheduleBlock({ event, cat }: { event: CalendarEvent; cat: CalCategory }) {
  if (!event.schedule) {
    return (
      <Card padded edge={false} style={{ borderStyle: "dashed", background: C.paperSoft }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 11,
              background: C.paperWarm,
              border: `1px dashed ${C.hairline}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icons.Cal s={18} c={C.inkMute} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Eyebrow style={{ color: C.inkMute }}>Schedule</Eyebrow>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 16,
                fontWeight: 700,
                color: C.inkSoft,
                letterSpacing: -0.2,
                marginTop: 1,
              }}
            >
              Not yet published
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: C.inkMute,
                fontWeight: 600,
                marginTop: 3,
                lineHeight: 1.45,
                fontFamily: F.body,
              }}
            >
              The running schedule appears once Operations have built it out in Events.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const { services, turns } = event.schedule;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Daily timetable */}
      <Card padded={false}>
        <div
          style={{
            padding: "14px 16px 8px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Eyebrow>Train services</Eyebrow>
            <div
              style={{
                fontFamily: F.serif,
                fontSize: 20,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: -0.3,
                marginTop: 2,
                lineHeight: 1.1,
              }}
            >
              Daily timetable
            </div>
          </div>
          <Stamp tone="brass">{services.length} runs</Stamp>
        </div>
        <DoubleRule style={{ margin: "0 16px" }} />
        <div>
          {services.map((s, i) => (
            <ServiceRow key={i} service={s} idx={i} />
          ))}
        </div>
        <div
          style={{
            padding: "10px 16px 14px",
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 11.5,
            color: C.inkMute,
            fontWeight: 600,
            lineHeight: 1.45,
            borderTop: `1px solid ${C.hairlineLt}`,
          }}
        >
          Booking counts will be live once MERAC ticket-sales sync ships.
          Crew assignment moves over once the Roster tab is live.
        </div>
      </Card>

      {turns && turns.length > 0 && (
        <Card padded={false}>
          <div
            style={{
              padding: "14px 16px 8px",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Eyebrow>Train turns</Eyebrow>
              <div
                style={{
                  fontFamily: F.serif,
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: -0.3,
                  marginTop: 2,
                  lineHeight: 1.1,
                }}
              >
                {turns.length} {turns.length === 1 ? "turn" : "turns"}
              </div>
            </div>
          </div>
          <div style={{ padding: "6px 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
            <p
              style={{
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 12.5,
                color: C.inkSoft,
                fontWeight: 600,
                lineHeight: 1.5,
                margin: "0 0 4px",
              }}
            >
              Locomotives grouped by their service sequence.
            </p>
            {turns.map((t, i) => (
              <TurnCard key={i} turn={t} cat={cat} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail hero band
// ---------------------------------------------------------------------------

function DetailHero({ event, cat }: { event: CalendarEvent; cat: CalCategory }) {
  const t = TONES[safeTone(cat.tone)];
  const heroStyle: CSSProperties = {
    position: "relative",
    padding: "22px 18px 24px",
    background: `linear-gradient(155deg, ${t.soft} 0%, ${C.paperSoft} 70%, ${C.paper} 100%)`,
    borderBottom: `1px solid ${C.hairlineLt}`,
    overflow: "hidden",
  };
  return (
    <div style={heroStyle}>
      {/* Diagonal stripe of category colour */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: -30,
          top: -20,
          width: 90,
          height: 90,
          background: `linear-gradient(135deg, ${t.solid} 0%, ${t.solid} 50%, transparent 50%)`,
          opacity: 0.18,
          transform: "rotate(45deg)",
        }}
      />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 3,
              background: t.solid,
              boxShadow: `0 0 0 2px ${t.bg}`,
            }}
          />
          <Eyebrow style={{ color: t.fg }}>{cat.label}</Eyebrow>
        </div>
        <h1
          style={{
            fontFamily: F.serif,
            fontSize: 30,
            fontWeight: 700,
            color: C.ink,
            margin: 0,
            letterSpacing: -0.5,
            lineHeight: 1.05,
          }}
        >
          {event.name}
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontFamily: F.body }}>
          <span style={{ fontFamily: F.mono, fontSize: 13, fontWeight: 700, color: C.brassDk, letterSpacing: 0.5 }}>
            {fmtRange(event.start, event.end)}
          </span>
          <span style={{ color: C.inkMute, fontWeight: 700 }}>·</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.inkSoft }}>
            {event.location.split("·")[0].trim()}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventDetail root -- Phase 9 / Cut 2: fetch by id, render-once.
// ---------------------------------------------------------------------------

export function EventDetail({ eventId, onBack }: { eventId: string; onBack: () => void }) {
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setLoadError(null);
    apiFetch<CalendarEvent>(`/api/calendar/events/${encodeURIComponent(eventId)}`)
      .then((res) => {
        if (!cancelled) setEvent(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        } else {
          setLoadError(e instanceof ApiError ? e.message : "Couldn't load the event.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // 404 -- treat distinctly per the kickoff: event was removed by a sync
  // between the list fetch and the user tapping the row. Specific copy +
  // Back button.
  if (notFound) {
    return <EventMissing onBack={onBack} />;
  }

  // Generic fetch error.
  if (loadError) {
    return <EventLoadError message={loadError} onBack={onBack} />;
  }

  // Loading -- full-screen skeleton with the back button wired so the user
  // can bail out without waiting.
  if (loading || !event) {
    return <EventDetailSkeleton onBack={onBack} />;
  }

  // Fall back to a brass-flavoured placeholder category if the event's
  // category string isn't in CAL_CATEGORIES -- defensive belt-and-braces
  // against a future Events row using a type we haven't taught the
  // front-end about.
  const cat: CalCategory =
    CAL_CATEGORIES[event.category] ?? {
      id: event.category,
      eventTypeId: event.eventTypeId,
      label: event.category,
      tone: "brass",
      short: "—",
    };

  return (
    <div>
      <TopBar
        title="Event"
        sub={cat.label}
        onBack={onBack}
        right={
          // Pin icon is a silent no-op in V1 per gotcha #15. Affordance
          // preserved to keep the visual contract with the prototype.
          <button
            type="button"
            aria-label="Pin event"
            onClick={() => { /* intentional no-op -- gotcha #15 */ }}
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              border: `1px solid ${C.hairline}`,
              background: C.card,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: C.shadowS,
              flexShrink: 0,
            }}
          >
            <Icons.Pin s={15} c={C.brassDk} />
          </button>
        }
      />
      <DetailHero event={event} cat={cat} />
      <div style={{ padding: "16px 16px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
        <KeyInfoCard event={event} cat={cat} />
        <ScheduleBlock event={event} cat={cat} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading / error frames -- share the TopBar so the back button stays where
// the user expects it across every state of the page.
// ---------------------------------------------------------------------------

function EventDetailSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div>
      <TopBar title="Event" onBack={onBack} />
      <div
        style={{
          padding: "20px 16px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
        aria-busy="true"
      >
        <SkeletonBar h={120} />
        <SkeletonBar h={240} />
        <SkeletonBar h={180} />
      </div>
    </div>
  );
}

function SkeletonBar({ h }: { h: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height: h,
        background: C.paperWarm,
        borderRadius: 14,
        border: `1px solid ${C.hairlineLt}`,
      }}
    />
  );
}

function EventMissing({ onBack }: { onBack: () => void }) {
  return (
    <div>
      <TopBar title="Event" onBack={onBack} />
      <div style={{ padding: "24px 16px 28px" }}>
        <Card>
          <Eyebrow>This event</Eyebrow>
          <div
            style={{
              fontFamily: F.serif,
              fontSize: 22,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.3,
              marginTop: 2,
            }}
          >
            No longer available
          </div>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 14,
              color: C.inkSoft,
              fontWeight: 600,
              margin: "12px 0 16px",
              lineHeight: 1.45,
            }}
          >
            The event may have been removed from the Events DB since the
            calendar last synced. Head back to the list to see what's still
            scheduled.
          </p>
          <Button variant="secondary" onClick={onBack}>Back to calendar</Button>
        </Card>
      </div>
    </div>
  );
}

function EventLoadError({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <div>
      <TopBar title="Event" onBack={onBack} />
      <div style={{ padding: "24px 16px 28px" }}>
        <div
          role="alert"
          style={{
            background: C.signalBg,
            border: "1px solid rgba(184,58,43,0.25)",
            borderRadius: 14,
            padding: 16,
            fontFamily: F.body,
            fontWeight: 700,
            color: C.signalDk,
          }}
        >
          Couldn't load the event.
          <div
            style={{
              marginTop: 4,
              fontWeight: 600,
              fontSize: 12,
              color: C.inkSoft,
            }}
          >
            {message}
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <Button variant="secondary" onClick={onBack}>Back to calendar</Button>
        </div>
      </div>
    </div>
  );
}
